import { AuthError } from "@/modules/auth/authErrorTypes"
import { getSessionInformations } from "@/modules/auth/utils/getSessionInformations"
import { registerFailedRefreshAttempt, resetFailedRefreshAttempts } from "@/modules/auth/utils/refreshTokenAttempts"
import { LoggerType } from "@/modules/utils/loggerUtils"
import { AuthError as AmplifyAuthError } from "aws-amplify/auth"

export const getValidToken = async (forceRefresh = false) => {
  try {
    const { idToken, isIdTokenValid } = await getSessionInformations(forceRefresh)

    if (isIdTokenValid && idToken) {
      console.log(
        LoggerType.Auth + `[Cognito] getValidToken succeeded ✅ (token valid, forceRefresh: ${String(forceRefresh)})`,
      )
      // Session is healthy again — clear any previously recorded refresh failures.
      resetFailedRefreshAttempts()
      return idToken
    }

    if (!isIdTokenValid && !forceRefresh) {
      console.error(LoggerType.Auth + "[Cognito] Token invalid ❌, retrying ↩️ with forceRefresh=true")
      // Try once more with forced refresh
      const { idToken: newIdToken, isIdTokenValid: newIsIdTokenValid } = await getSessionInformations(true)

      if (newIsIdTokenValid && newIdToken) {
        console.error(LoggerType.Auth + `[Cognito] getValidToken succeeded ✅ (token refreshed and valid)`)
        resetFailedRefreshAttempts()
        return newIdToken
      }
    }

    console.warn(
      LoggerType.Auth +
        `[Cognito] getValidToken failed ❌ (no valid token found — unauthenticated or fully expired session)`,
    )

    // Unauthenticated or fully expired session — expected condition, not an exception.
    // Return undefined so the request proceeds without Authorization; server returns 401 and app redirects to login.
    return undefined
  } catch (e) {
    if (e instanceof AmplifyAuthError && e.name === AuthError.NetworkError) {
      console.warn(LoggerType.Auth + `[Cognito] getValidToken network error ❌ (ignoring, returning undefined)`)

      // throw an error means that we will be disconected, so we ignore 'network error' (no network) by returning undefined
      return undefined
    }

    if (e instanceof AmplifyAuthError && e.name === AuthError.UserLambdaValidationException) {
      console.warn(
        LoggerType.Auth +
          `[Cognito] getValidToken failed ❌ — UserLambdaValidationException (transient infra, ignoring, returning undefined)`,
      )

      // Cognito Pre-Token Generation Lambda failed (502/503 or Lambda timeout) — transient infra error, not a disconnect
      return undefined
    }

    if (e instanceof AmplifyAuthError) {
      console.warn(
        LoggerType.Auth +
          `[Cognito] getValidToken failed ❌ — ${e.name} (already captured in getSessionInformations, returning undefined)`,
      )

      // Any other Amplify auth error (e.g. NotAuthorizedException — refresh token expired/revoked)
      // indicates a failed refresh attempt. Count it: once the limit is reached the user is forced
      // to log out. Return undefined so the in-flight request proceeds without Authorization;
      // server returns 401 and the app redirects to login. Already reported to Sentry upstream.
      void registerFailedRefreshAttempt()
      return undefined
    }

    console.warn(
      LoggerType.Auth +
        `[Cognito] getValidToken failed ❌ — unexpected non-Amplify error (${e instanceof Error ? e.message : String(e)}), returning undefined`,
    )

    // Non-AmplifyAuthError (e.g. unexpected SDK internal error) — treat same as unauthenticated:
    // return undefined so request proceeds without Authorization; server returns 401 and app redirects to login.
    // Previously threw HUError("No token") here, which surfaced as Sentry noise WEB-1YZ / WEB-1VT / WEB-264.
    return undefined
  }
}
